import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogTrigger,
} from '@/components/ui/dialog';
import DataTable, { Column, FilterOption } from '@/components/ui/data-table';
import toast from 'react-hot-toast';
import DateTimeFormatter from '@/components/shared/DateTimeFormatter';
import { Building, Globe, Plus } from 'lucide-react';
import httpClient from '@/api/httpClient';
import { CustomDomainStatus, CustomDomainStatusBadge } from '@/components/admin/CustomDomainStatus';

interface Tenant {
    id: string;
    tenant_slug: string;
    tenant_name: string;
    system_name: string;
    custom_domain?: string;
    custom_domain_status?: CustomDomainStatus | null;
    is_active: boolean;
    is_admin_tenant: boolean;
    created_at: string;
    updated_at: string;
}

export function TenantManagementPage() {
    const navigate = useNavigate();
    const [tenants, setTenants] = useState<Tenant[]>([]);
    const [isLoading, setIsLoading] = useState(true);
    const [isCreatingTenant, setIsCreatingTenant] = useState(false);

    // DataTable state
    const [tenantFilters, setTenantFilters] = useState<Record<string, any>>({});
    const [tenantSortConfig, setTenantSortConfig] = useState<{
        key: keyof Tenant;
        direction: 'asc' | 'desc';
    }>({ key: 'created_at', direction: 'desc' });
    const [selectedTenants, setSelectedTenants] = useState<string[]>([]);
    // Toast notifications using react-hot-toast

    // Fetch tenants
    const fetchTenants = async () => {
        try {
            const response = await httpClient.get('/tenant-manage/tenants');
            const data = response.data;
            if (data.success) {
                setTenants(data.data);
            }
        } catch (error) {
            console.error('Failed to fetch tenants:', error);
            toast.error('Failed to fetch tenants');
        }
    };

    useEffect(() => {
        const loadData = async () => {
            setIsLoading(true);
            await fetchTenants();
            setIsLoading(false);
        };

        loadData();
    }, []);

    // Tenant columns
    const tenantColumns: Column<Tenant>[] = [
        {
            id: 'tenant_name',
            key: 'tenant_name',
            label: 'Tenant Name',
            render: (_, row) => (
                <div className="flex items-center space-x-2">
                    <Building className="h-4 w-4" />
                    <span className="font-medium">{row.tenant_name}</span>
                    {row.is_admin_tenant && <Badge variant="secondary">Admin</Badge>}
                </div>
            ),
        },
        {
            id: 'tenant_slug',
            key: 'tenant_slug',
            label: 'Slug',
            render: (_, row) => (
                <code className="text-sm bg-gray-100 px-2 py-1 rounded">{row.tenant_slug}</code>
            ),
        },
        {
            id: 'system_name',
            key: 'system_name',
            label: 'System Name',
            render: (_, row) => <span>{row.system_name}</span>,
        },
        {
            id: 'custom_domain',
            key: 'custom_domain',
            label: 'Custom Domain',
            render: (_, row) =>
                row.custom_domain ? (
                    <div className="flex items-center space-x-2">
                        <Globe className="h-4 w-4" />
                        <span>{row.custom_domain}</span>
                        <CustomDomainStatusBadge status={row.custom_domain_status} />
                    </div>
                ) : (
                    <span className="text-gray-500">-</span>
                ),
        },
        {
            id: 'is_active',
            key: 'is_active',
            label: 'Status',
            render: (_, row) => (
                <Badge variant={row.is_active ? 'default' : 'secondary'}>
                    {row.is_active ? 'Active' : 'Inactive'}
                </Badge>
            ),
        },
        {
            id: 'created_at',
            key: 'created_at',
            label: 'Created',
            render: (_, row) => <DateTimeFormatter date={row.created_at} />,
        },
    ];

    // Tenant filters
    const tenantFilterOptions: FilterOption[] = [
        {
            key: 'tenant_name',
            label: 'Tenant Name',
            type: 'input',
            placeholder: 'Search by tenant name',
        },
        {
            key: 'tenant_slug',
            label: 'Slug',
            type: 'input',
            placeholder: 'Search by slug',
        },
        {
            key: 'is_active',
            label: 'Status',
            type: 'select',
            options: [
                { label: 'Active', value: 'true' },
                { label: 'Inactive', value: 'false' },
            ],
        },
        {
            key: 'is_admin_tenant',
            label: 'Type',
            type: 'select',
            options: [
                { label: 'Admin', value: 'true' },
                { label: 'Regular', value: 'false' },
            ],
        },
    ];

    // Event handlers
    const handleCreateTenant = () => {
        setIsCreatingTenant(true);
    };

    if (isLoading) {
        return (
            <div className="flex items-center justify-center h-64">
                <div className="text-center">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900 mx-auto"></div>
                    <p className="mt-2 text-gray-600">Loading tenant management...</p>
                </div>
            </div>
        );
    }

    return (
        <div className="space-y-6">
            <div className="flex items-center justify-between">
                <div>
                    <h1 className="text-3xl font-bold">Tenant Management</h1>
                    <p className="text-gray-600">Manage tenants and tenant managers</p>
                </div>
                <div className="flex space-x-2">
                    <Dialog open={isCreatingTenant} onOpenChange={setIsCreatingTenant}>
                        <DialogTrigger asChild>
                            <Button>
                                <Plus className="h-4 w-4 mr-2" />
                                Create Tenant
                            </Button>
                        </DialogTrigger>
                        <DialogContent>
                            <DialogHeader>
                                <DialogTitle>Create New Tenant</DialogTitle>
                            </DialogHeader>
                            <CreateTenantForm
                                onSuccess={() => {
                                    setIsCreatingTenant(false);
                                    fetchTenants();
                                }}
                                onCancel={() => setIsCreatingTenant(false)}
                            />
                        </DialogContent>
                    </Dialog>
                </div>
            </div>

            <Card>
                <CardHeader>
                    <CardTitle>Tenants</CardTitle>
                </CardHeader>
                <CardContent>
                    <DataTable
                        data={tenants}
                        columns={tenantColumns}
                        isLoading={isLoading}
                        filters={tenantFilters}
                        onFilterChange={setTenantFilters}
                        sortConfig={tenantSortConfig}
                        onSort={key =>
                            setTenantSortConfig(prev => ({
                                key,
                                direction:
                                    prev.key === key && prev.direction === 'asc' ? 'desc' : 'asc',
                            }))
                        }
                        selectedItems={selectedTenants}
                        onSelectionChange={setSelectedTenants}
                        getItemId={item => item.id}
                        onRowClick={tenant => navigate(`/tenants/${tenant.id}`)}
                        filterOptions={tenantFilterOptions}
                        emptyMessage="No tenants found"
                        loadingMessage="Loading tenants..."
                    />
                </CardContent>
            </Card>
        </div>
    );
}

// Create Tenant Form Component
function CreateTenantForm({
    onSuccess,
    onCancel,
}: {
    onSuccess: () => void;
    onCancel: () => void;
}) {
    const [formData, setFormData] = useState({
        tenant_slug: '',
        tenant_name: '',
        system_name: '',
        custom_domain: '',
        admin_name: '',
        admin_email: '',
        admin_password: '',
    });
    const [isSubmitting, setIsSubmitting] = useState(false);
    // Toast notifications using react-hot-toast

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setIsSubmitting(true);

        try {
            const response = await httpClient.post('/tenant-manage/tenants', formData);
            const data = response.data;
            if (data.success) {
                toast.success('Tenant created successfully');
                onSuccess();
            } else {
                throw new Error(data.message || 'Failed to create tenant');
            }
        } catch (error) {
            console.error('Failed to create tenant:', error);
            toast.error('Failed to create tenant');
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
                <div>
                    <Label htmlFor="tenant_slug">Tenant Slug</Label>
                    <Input
                        id="tenant_slug"
                        value={formData.tenant_slug}
                        onChange={e => setFormData({ ...formData, tenant_slug: e.target.value })}
                        placeholder="my-tenant"
                        required
                    />
                </div>
                <div>
                    <Label htmlFor="tenant_name">Tenant Name</Label>
                    <Input
                        id="tenant_name"
                        value={formData.tenant_name}
                        onChange={e => setFormData({ ...formData, tenant_name: e.target.value })}
                        placeholder="My Tenant"
                        required
                    />
                </div>
            </div>

            <div>
                <Label htmlFor="system_name">System Name</Label>
                <Input
                    id="system_name"
                    value={formData.system_name}
                    onChange={e => setFormData({ ...formData, system_name: e.target.value })}
                    placeholder="My Trading Platform"
                    required
                />
            </div>

            <div>
                <Label htmlFor="custom_domain">Custom Domain (Optional)</Label>
                <Input
                    id="custom_domain"
                    value={formData.custom_domain}
                    onChange={e => setFormData({ ...formData, custom_domain: e.target.value })}
                    placeholder="example.com"
                />
            </div>

            <div className="border-t pt-4">
                <h3 className="text-lg font-medium mb-4">Admin User</h3>
                <div className="grid grid-cols-2 gap-4">
                    <div>
                        <Label htmlFor="admin_name">Admin Name</Label>
                        <Input
                            id="admin_name"
                            value={formData.admin_name}
                            onChange={e => setFormData({ ...formData, admin_name: e.target.value })}
                            placeholder="Admin User"
                            required
                        />
                    </div>
                    <div>
                        <Label htmlFor="admin_email">Admin Email</Label>
                        <Input
                            id="admin_email"
                            type="email"
                            value={formData.admin_email}
                            onChange={e =>
                                setFormData({ ...formData, admin_email: e.target.value })
                            }
                            placeholder="admin@example.com"
                            required
                        />
                    </div>
                </div>
                <div className="mt-4">
                    <Label htmlFor="admin_password">Admin Password</Label>
                    <Input
                        id="admin_password"
                        type="password"
                        value={formData.admin_password}
                        onChange={e => setFormData({ ...formData, admin_password: e.target.value })}
                        placeholder="Password"
                        required
                    />
                </div>
            </div>

            <div className="flex justify-end space-x-2">
                <Button type="button" variant="outline" onClick={onCancel}>
                    Cancel
                </Button>
                <Button type="submit" disabled={isSubmitting}>
                    {isSubmitting ? 'Creating...' : 'Create Tenant'}
                </Button>
            </div>
        </form>
    );
}
